<template>
  <div
    ref="productCard"
    class="product-card"
    :class="{
      [cardClass!]: Boolean(cardClass),
      'out-of-stock': isOutOfStock
    }"
    :data-product-id="product.id">
    <NuxtLink class="product-link" :to="productLink" @click="trackClick">
      <span class="img-wrapper">
        <div v-if="isOutOfStock" class="out-of-stock-overlay">
          <span v-if="product.temporarily_out_of_stock">
            {{ t('product.temporary_out_of_stock') }}
          </span>
          <span v-else>
            {{ t('product.out_of_stock') }}
          </span>
        </div>
        <ProductImg
          v-if="product.image"
          :src="product.image"
          class="product-img"
          size="md"
          :alt="`${product.manufacturer} ${product.name}`" />
      </span>
      <PromoSticker :enabled="product.sticker_enabled" :src="product.sticker_img" no-side-padding />
      <span v-if="discount" class="discount-sticker">
        <Sticker>
          {{ discount }}
        </Sticker>
      </span>
      <span class="product-details flex flex-col flex-1">
        <span class="manufacturer line-clamp-1"> {{ product.manufacturer }}</span>
        <span class="title overflow-hidden h-[2.5rem] lg:text-lg lg:h-[2.75rem]">{{ product.name }}</span>
        <ProductCardPrice
          v-if="product.in_stock"
          :old-price="product.old_price"
          :current-price="product.current_price" />
        <span v-if="!storeConfig.getIsMobile && !isAlt" class="link-section flex items-end flex-1">
          <Button class="btn-secondary w-full" :to="productLink" @click="trackClick">
            {{ texts.view }}
          </Button>
        </span>
      </span>
    </NuxtLink>
    <ProductCardActions
      :product="product"
      :component-hint="props.componentHint"
      :search-text="props.searchText"
      :query-key="props.queryKey"
      :index="index"
      @on-add-to-cart="onAddToCart" />
  </div>
</template>

<script lang="ts" setup>
import { useStoreConfig } from '~/store/storeConfig'
import buildProductUrl from '@/utils/routing/buildProductUrl'
import getDiscount from '@/utils/getDiscount'

import type { Product } from '~/types/searchTypes'
import { useAnalyticalDataTracking } from '~/composables/analytics/useAnalyticalDataTracking'
import { InteractionPlaceComponentNames } from '~/types/analytics/InteractionPlace'
import { useIntersectionObserver } from '@vueuse/core'
import QueryEnum from '~/enums/QueryEnum'
import ProductCardPrice from '~/components/Product/ProductCardPrice.vue'
import Sticker from '~/components/Product/Sticker.vue'
import ProductImg from '~/components/Product/ProductImg.vue'
import type { AddedItemInterface } from '~/types/cartTypes'
import ProductCardActions from '~/components/Product/ProductCardActions.vue'

const props = defineProps<{
  product: Product
  cardClass?: string
  index: number
  queryKey?: string | undefined // Used for tracking
  searchText?: string | undefined // Used for tracking,
  componentHint?: InteractionPlaceComponentNames | undefined
  redirectUrl?: string
}>()

const storeConfig = useStoreConfig()
const nuxtApp = useNuxtApp()
const productId = useState('productId')
const route = useRoute()
const { t } = useLang()
const { isAlt } = useTheme()
const discount = computed(() => getDiscount(props.product.current_price, props.product.old_price))

const emit = defineEmits(['visible', 'onAddToCart'])

const productCard = ref<HTMLElement | null>(null)

const targetIsVisible = ref(false)

const observer = useIntersectionObserver(
  productCard,
  ([{ intersectionRatio }], _observerElement) => {
    if (intersectionRatio >= 0.85 && targetIsVisible.value === false) {
      targetIsVisible.value = true

      emit('visible')

      observer.stop()
    }
  },
  {
    threshold: [0, 0.25, 0.5, 0.75, 1],
  }
)

async function trackClick(): Promise<void> {
  if (route.meta.key === 'category' || route.meta.key === 'search') {
    productId.value = props.product.id
  }

  await useAnalyticalDataTracking().trackMarketplaceProductClick({
    id: props.product.id.toString(),
    category: props.product.category,
    index: props.index,
    manufacturer: props.product.manufacturer,
    name: props.product.name,
    price: props.product.current_price,
    sku: props.product.sku,
    interaction_place: {
      componentName: InteractionPlaceComponentNames.MarketplaceProductCard,
    },
  })
}

nuxtApp.hook('page:transition:finish', () => {
  if (productCard.value && props.product.id === productId.value) {
    window?.scrollTo({
      top: productCard?.value?.offsetTop,
      behavior: storeConfig.isMobile ? 'smooth' : 'instant',
    })
  }
})

const productLink = computed(() => {
  if (props.redirectUrl && isValidRedirectUrl(props.redirectUrl)) {
    return props.redirectUrl
  }

  const location = buildProductUrl(props.product.quote_slug, props.product.id)

  if (typeof location !== 'string' && QueryEnum.ComponentHint) {
    location.query = {
      ...location.query,
      [QueryEnum.QueryKey]: props.queryKey,
      [QueryEnum.SearchText]: props.searchText,
      [QueryEnum.ComponentHint]: props.componentHint,
    }
  }

  return location
})

const isOutOfStock = computed(() => {
  if (typeof props.product.in_stock === 'undefined') {
    return false
  }

  return !props.product.in_stock
})

function isValidRedirectUrl(string: string | null): boolean {
  try {
    const url = new URL(String(string))

    return url.protocol === 'https:'
  } catch (err) {
    return false
  }
}

const texts = computed(() => {
  return {
    view: t('common.view'),
  }
})

const onAddToCart = (data: AddedItemInterface) => {
  emit('onAddToCart', data)
}
</script>

<style lang="postcss" scoped>
.product-card {
  @apply relative w-full flex flex-col p-4 h-full outline outline-1 rounded-sm outline-gray2;
  @apply dark:bg-dmGray1 dark:outline-none;

  &.out-of-stock {
    .product-img {
      @apply opacity-30;
    }
  }

  .out-of-stock-overlay {
    @apply absolute top-0 left-0 h-full w-full flex items-center justify-center text-brand-primary text-xl lg:text-3xl uppercase z-[1] font-bold text-center font-primary;
  }

  .img-wrapper {
    @apply flex items-center relative h-[19.4375rem] bg-white rounded-sm overflow-hidden;

    .product-img {
      @apply my-0 mx-auto object-contain max-w-full max-h-full w-full h-full;
    }
  }

  &.grid-card {
    @apply justify-end;

    .img-wrapper {
      @apply h-[9.375rem] lg:h-[20.625rem];
    }
  }

  .discount-sticker {
    @apply absolute top-0 left-0 dark:-top-2 dark:-left-2;
  }
}

.product-card {
  .product-link {
    display: flex;
    flex-direction: column;
    text-decoration: none;
    height: 100%;
    position: relative;
  }

  .product-details {
    @apply pt-[0.625rem];

    .title {
      @apply overflow-hidden text-ellipsis break-words;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .manufacturer {
      @apply text-gray6 dark:text-dmText3 font-semibold break-words;
      height: 20px;
    }
  }

  &:hover {
    @apply shadow-lg z-[1] dark:shadow-dmGray4;
  }
}

.pj {
  .product-card {
    @apply rounded-none p-2 hover:z-[1];

    .manufacturer {
      @apply uppercase pb-2 text-red;
    }
  }
}

.tp {
  .product-card {
    @apply rounded-none p-2;

    .manufacturer {
      @apply uppercase pb-2 text-brand-primary font-bold;
    }
  }
}

.wl {
  .product-card {
    @apply bg-white dark:bg-dmGray1 border-none lg:p-4;

    .img-wrapper {
      @apply rounded-sm border border-neutral5 bg-neutral7 p-4;

      img {
        @apply mix-blend-multiply;
      }
    }

    .manufacturer {
      @apply !normal-case pb-2 !text-text2 dark:text-dmText2;
    }
  }
}
</style>
